/**
 * Autopilot: the "sensible player" between runs (PLAN §11 loop step 2), rebuilt on the Altar
 * Forge (crafting.md §11). Used by the harness progression sim, by the web client for bot
 * companions, and as an "optimise everything" button. Deterministic: it walks characters, slots
 * and items in a fixed order and only goes through the public operations (forge rolls draw from
 * the profile's forge RNG stream), so the same rank gates and odds apply as for a human.
 *
 * Who pays: the main character forges from the player's wallet (and gold); every other
 * character forges from its own satchel, at base odds and for no gold (bots never drain the
 * player's purse). The main character never spends Venom, Eyes or Blessed Charges unless it is
 * auto-managed or `reagents` is set.
 *
 * Order: claim the vault → equip the best bag items → combine obsolete Scales → craft slots a
 * craft would beat by ≥ CRAFT_GAIN item levels (Eye crafts for a legendary) → salvage the bag →
 * tier sets (Catalyst / Sigil crafts: main first, then companions once shared) → temper the
 * lowest item first → fuse + socket gems → reforge capped items → masterwork (surplus only) →
 * recommended talents.
 */
import type { ClassId } from '@keystone/sim';
import { CRAFT_SCALES, GRADE_BAND_MAX, GRADE_CRAFT_ILVL, MW_COST, TEMPER_COST, TIER_SET_IDS, gradeForIlvl } from './forge.constants';
import { masterworkCeiling } from './forge.items';
import { craftCost, eyeAttuneOdds, refineCost, sigilReshapeOdds, temperOdds } from './forge.odds';
import type { CraftSpec } from './forge.odds';
import { attuneEye, catalyze, combineScales, craft, discardRefine, keepRefine, masterworkItem, refine, salvage, sigilReshape, temper } from './forge';
import type { CraftRoll, ForgeRoll } from './forge';
import { ROLE_GEM_COLOR, fuseGoldCost } from './gems';
import { effectiveIlvl, itemScore } from './items';
import { CLASS_STAT_PROFILES, legendary } from './data';
import { MAX_EQUIPPED_LEGENDARIES, legendariesFor } from './items.legendaries';
import {
  autoGemCharacter,
  claimVault,
  equipItem,
  equippedGear,
  fuseGems,
  itemById,
  itemOwner,
  profileMaxGemRank,
  profileRating,
  profileStanding,
  roleOf,
  reforgeItem,
  unequippedItems,
  wearIfBetter,
} from './profile';
import { RANK_TIERS, hasUnlock, tierDef } from './rating';
import { setPieces } from './sets';
import { defaultLoadout } from './talents';
import type { Item, ItemSlot, Profile, Rarity, ScaleGrade, SecondaryStat, VaultOptionKind } from './types';
import { ITEM_SLOTS, SCALE_GRADES, SECONDARY_STATS, TIER_SLOTS } from './types';
import { REFORGE_GOLD, reforge } from './upgrade';
import { EYE_ATTUNE_FRAGMENTS, EYE_FRAGMENTS_PER_EYE, QUALITY_MAX, REFORGE_FANGSTEEL, TEMPER_VENOM_MAX, TEMPER_VENOM_PP } from './forge.constants';

/** A craft must beat the current piece by this many item levels (crafting.md §11). */
export const CRAFT_GAIN = 10;
/** Venom the auto-forge commits per craft when reagents are allowed. */
export const AUTO_CRAFT_VENOM = 4;
/** Venom the main keeps for crafts before spending it on temper odds. */
export const TEMPER_VENOM_RESERVE = 2 * AUTO_CRAFT_VENOM;
/** Moltskin per auto-craft stat lock (2 = both secondaries from the class priority). */
export const AUTO_CRAFT_MOLTSKIN = 2;
/** Rarity a capped item is re-crafted toward on the plateau (main, with Venom); companions aim for Rare. */
export const RECRAFT_TARGET: Readonly<Record<'wallet' | 'satchel', Rarity>> = { wallet: 'epic', satchel: 'rare' };
/** Share of the tier-up reserve a plateau re-craft leaves untouched (a player banks some, rolls some). */
export const RECRAFT_RESERVE_FRACTION = 0.5;
/** Re-crafts per character per autopilot pass. */
export const RECRAFT_PER_PASS = 1;
/** Sigils kept in reserve when sharing with companions. */
export const SIGIL_RESERVE = 1;
/** +econ-pacing: Sigils kept back from Sigil Reshape (a new tier slot or a companion may still want one). */
export const RESHAPE_SIGIL_RESERVE = 2;
/** +econ-pacing: Sigil Reshapes / Moltskin Refines per autopilot pass (the late plateau sinks). */
export const RESHAPES_PER_PASS = 1;
export const REFINES_PER_PASS = 2;
/** +econ-pacing: Moltskin kept back from Refine for craft stat locks. */
export const REFINE_MOLTSKIN_RESERVE = 2 * AUTO_CRAFT_MOLTSKIN;
/** Companion order for shared Sigils (crafting.md §11). */
const ROLE_ORDER = ['tank', 'healer', 'dps'] as const;
/** Safety bound on forge attempts per pass. */
const MAX_ATTEMPTS = 400;

export interface AutopilotOptions {
  /** Characters to manage (default: every character). */
  classes?: readonly ClassId[];
  /** Temper equipped gear (default true). Old name kept for callers. */
  upgrade?: boolean;
  /** Craft new pieces (default true). */
  craft?: boolean;
  /** Salvage unequipped non-legendary items (default true). */
  scrap?: boolean;
  /** Re-pick the recommended talent loadout (default true). */
  talents?: boolean;
  /** Reforge items at the tier cap toward the class's stat priority (default true). */
  reforge?: boolean;
  /** Masterwork surplus (default true). */
  masterwork?: boolean;
  /** Claim the vault (default: when the main character is managed). */
  vault?: boolean;
  /** Spend Sigils on tier pieces (default true). */
  sigils?: boolean;
  /** Let the main character spend Venom / Eyes / Blessed (default: when it is auto-managed). */
  reagents?: boolean;
  /** Observer: called after every roll the pass makes (harness stats; never changes outcomes). */
  onRoll?: (e: AutopilotRollEvent) => void;
}

/** One forge roll the autopilot made (for the harness's pity / near-miss statistics). */
export type AutopilotRollEvent =
  | { kind: 'craft'; classId: ClassId; payer: 'wallet' | 'satchel'; roll: CraftRoll }
  | { kind: 'temper' | 'masterwork'; classId: ClassId; payer: 'wallet' | 'satchel'; roll: ForgeRoll };

export interface AutopilotReport {
  profile: Profile;
  equipped: number;
  /** Items salvaged (old name kept). */
  scrapped: number;
  /** Successful temper steps (old name kept: an "upgrade" is now a temper). */
  upgradeSteps: number;
  temperAttempts: number;
  temperFails: number;
  crafts: number;
  /** Tier pieces made (Catalyst or Sigil craft). */
  tierPieces: number;
  combines: number;
  gemsSocketed: boolean;
  fuses: number;
  reforges: number;
  masterworkSteps: number;
  /** +econ-pacing: Sigil Reshapes and Moltskin Refines made (late sinks). */
  reshapes: number;
  refines: number;
  /** Gold spent this pass (autopilot never earns gold). */
  goldSpent: number;
  /** A forge action was affordable in materials but not in gold (gold starvation signal). */
  goldBlocked: boolean;
  vaultClaimed: VaultOptionKind | null;
}

const RARITY_RANK: readonly Rarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary'];
const capOf = (p: Profile): number => tierDef(profileStanding(p).tier).maxIlvl;
const payerOf = (p: Profile, classId: ClassId): 'wallet' | 'satchel' => (classId === p.mainClassId ? 'wallet' : 'satchel');

/** Scales a payer holds. */
function scalesOf(p: Profile, classId: ClassId): Record<ScaleGrade, number> {
  return payerOf(p, classId) === 'wallet' ? p.wallet.scales : (p.characters[classId]?.satchel?.scales ?? { ashen: 0, jade: 0, gilded: 0, eternal: 0 });
}

/** Best grade (highest craft ilvl under the cap first) a payer can craft `slot` at right now. */
export function bestCraftGrade(p: Profile, classId: ClassId, slot: ItemSlot): ScaleGrade | null {
  const cap = capOf(p);
  const have = scalesOf(p, classId);
  const fs = payerOf(p, classId) === 'wallet' ? p.wallet.fangsteel : (p.characters[classId]?.satchel?.fangsteel ?? 0);
  if (fs < craftCost(slot, 'ashen').fangsteel) return null;
  let best: ScaleGrade | null = null;
  for (const g of SCALE_GRADES) {
    // Trade-down: a craft at g may be paid with g or higher.
    let n = 0;
    for (const h of SCALE_GRADES.slice(SCALE_GRADES.indexOf(g))) n += have[h];
    if (n < CRAFT_SCALES) continue;
    if (best && Math.min(cap, GRADE_CRAFT_ILVL[g]) <= Math.min(cap, GRADE_CRAFT_ILVL[best])) continue;
    best = g;
  }
  return best;
}

/**
 * The auto-craft helper: craft `slot` for `classId` at its best grade if that beats the current
 * piece by ≥ CRAFT_GAIN item levels (or the slot is empty), and wear the result if it scores
 * higher. Returns null when no craft is worth it or affordable.
 */
export function autoCraft(p: Profile, classId: ClassId, slot: ItemSlot, extra: Partial<CraftSpec> = {}): { profile: Profile; item: Item; equipped: boolean; roll: CraftRoll } | null {
  const grade = bestCraftGrade(p, classId, slot);
  if (!grade) return null;
  const cur = itemById(p, p.characters[classId]?.gear[slot]);
  const ilvl = Math.min(capOf(p), GRADE_CRAFT_ILVL[grade]);
  if (cur && !extra.eye && ilvl < effectiveIlvl(cur) + CRAFT_GAIN) return null;
  const payer = payerOf(p, classId);
  const spec: CraftSpec = { classId, slot, grade, payer, ...extra };
  if (payer === 'satchel') {
    delete spec.sigil;
    delete spec.eye;
    delete spec.blessed;
    spec.venom = Math.min(spec.venom ?? 0, p.characters[classId]?.satchel?.venom ?? 0);
  }
  const r = craft(p, spec);
  if (!r.ok) return null;
  const after = wearIfBetter(r.profile, classId, r.item.id);
  return { profile: after, item: r.item, equipped: after.characters[classId]?.gear[slot] === r.item.id, roll: r.roll };
}

export function autopilot(profile: Profile, opts: AutopilotOptions = {}): AutopilotReport {
  let p = profile;
  const classes = [...(opts.classes ?? Object.keys(p.characters))].sort();
  const mainManaged = classes.includes(p.mainClassId);
  const reagents = opts.reagents ?? !!p.characters[p.mainClassId]?.autoManaged;
  const rep: Omit<AutopilotReport, 'profile' | 'goldSpent'> = {
    equipped: 0,
    scrapped: 0,
    upgradeSteps: 0,
    temperAttempts: 0,
    temperFails: 0,
    crafts: 0,
    tierPieces: 0,
    combines: 0,
    gemsSocketed: false,
    fuses: 0,
    reforges: 0,
    masterworkSteps: 0,
    reshapes: 0,
    refines: 0,
    goldBlocked: false,
    vaultClaimed: null,
  };
  const goldBefore = p.currencies.gold;

  // 1. Vault: the Altar Cache (a Sigil) while a managed character's set is under 4 pieces
  //    (companions only once Sigils are shared), then the free Forge Token.
  if ((opts.vault ?? mainManaged) && p.vault.offer) {
    const want: VaultOptionKind = wantsSigils(p, classes) ? 'altar' : 'token';
    const idx = Math.max(0, p.vault.offer.options.findIndex((o) => o.kind === want));
    rep.vaultClaimed = p.vault.offer.options[idx]?.kind ?? null;
    p = claimVault(p, idx);
  } else if ((opts.vault ?? mainManaged) && p.vault.pending?.options.length) {
    const pending = p.vault.pending;
    let best = 0;
    pending.options.forEach((o, i) => {
      if (itemScore(o, pending.forClassId) > itemScore(pending.options[best]!, pending.forClassId)) best = i;
    });
    p = claimVault(p, best);
  }

  // 2. Equip: each character takes the best bag item per slot (bag scanned in inventory order).
  for (const classId of classes) {
    for (const slot of ITEM_SLOTS) {
      const cur = itemById(p, p.characters[classId]?.gear[slot]);
      let best: Item | undefined;
      for (const it of unequippedItems(p)) {
        if (it.slot !== slot || !wearable(p, classId, it)) continue;
        const s = itemScore(it, classId);
        if (s > (best ? itemScore(best, classId) : cur ? itemScore(cur, classId) : -Infinity)) best = it;
      }
      if (!best) continue;
      const r = equipItem(p, classId, best.id);
      if (r.ok) {
        p = r.profile;
        rep.equipped++;
      }
    }
  }

  // 3. Combine Scales of grades nothing will spend any more (every managed item is past the
  //    band and a craft at that grade is beaten by a higher one under the cap).
  for (const classId of classes) {
    const payer = payerOf(p, classId);
    if (payer === 'wallet' && !mainManaged) continue;
    const owners = [classId];
    const cap = capOf(p);
    for (const g of SCALE_GRADES.slice(0, -1)) {
      const next = SCALE_GRADES[SCALE_GRADES.indexOf(g) + 1]!;
      const stillTempering = owners.some((c) => ITEM_SLOTS.some((s) => {
        const it = itemById(p, p.characters[c]?.gear[s]);
        return !it || (it.ilvl < GRADE_BAND_MAX[g] && it.ilvl < cap);
      }));
      const craftUseful = Math.min(cap, GRADE_CRAFT_ILVL[next]) <= Math.min(cap, GRADE_CRAFT_ILVL[g]);
      if (stillTempering || craftUseful) continue;
      const n = Math.floor(scalesOf(p, classId)[g] / 3);
      if (n <= 0) continue;
      const r = combineScales(p, g, n, payer, classId);
      if (r.ok) {
        p = r.profile;
        rep.combines += n;
      } else if (r.reason === 'notEnoughGold') rep.goldBlocked = true;
    }
  }

  // 4. Craft: Eye legendaries (main, Champion+), then any slot a craft beats by CRAFT_GAIN.
  if (opts.craft !== false) {
    for (const classId of classes) {
      const payer = payerOf(p, classId);
      const useReagents = payer === 'wallet' && reagents;
      if (useReagents && p.wallet.eyeFragments >= 5 && hasUnlock(profileRating(p), 'legendaries')) {
        const legs = legendariesFor(classId);
        const owned = new Set(p.inventory.map((i) => i.legendaryId).filter(Boolean));
        const wearing = ITEM_SLOTS.some((s) => itemById(p, p.characters[classId]?.gear[s])?.legendaryId);
        const target = legs.find((l) => !owned.has(l.id)) ?? (wearing ? undefined : legs[0]);
        if (target && !wearing) {
          p = attuneForEye(p); // +econ-pacing
          const r = autoCraft(p, classId, target.slot, { eye: true, ...(p.wallet.venom >= AUTO_CRAFT_VENOM ? { venom: AUTO_CRAFT_VENOM } : {}) });
          if (r) {
            p = r.profile;
            rep.crafts++;
            opts.onRoll?.({ kind: 'craft', classId, payer, roll: r.roll });
          }
        }
      }
      // Vault Forge Tokens (free craft, Rare floor, +Luck): on the lowest slot the token's item
      // level beats; held otherwise (a later tier cap or slot makes it worth more).
      if (payer === 'wallet' && mainManaged) {
        for (let t = p.wallet.vaultTokens.length - 1; t >= 0; t--) {
          const tok = p.wallet.vaultTokens[t]!;
          const ilvl = Math.min(capOf(p), GRADE_CRAFT_ILVL[tok.grade]);
          let slot: ItemSlot | undefined;
          let low = Infinity;
          for (const s of ITEM_SLOTS) {
            const cur = itemById(p, p.characters[classId]?.gear[s]);
            const e = cur ? effectiveIlvl(cur) : -Infinity;
            if (cur?.legendaryId || e >= ilvl || e >= low) continue;
            slot = s;
            low = e;
          }
          if (!slot) continue;
          const spec: CraftSpec = { classId, slot, grade: tok.grade, token: t, payer };
          if (useReagents && p.wallet.venom >= AUTO_CRAFT_VENOM) spec.venom = AUTO_CRAFT_VENOM;
          const r = craft(p, spec);
          if (!r.ok) continue;
          p = wearIfBetter(r.profile, classId, r.item.id);
          rep.crafts++;
          if (p.characters[classId]?.gear[slot] === r.item.id) rep.equipped++;
          opts.onRoll?.({ kind: 'craft', classId, payer, roll: r.roll });
        }
      }
      for (const slot of ITEM_SLOTS) {
        const extra: Partial<CraftSpec> = {};
        if (useReagents) {
          if (p.wallet.venom >= AUTO_CRAFT_VENOM) extra.venom = AUTO_CRAFT_VENOM;
          if (p.wallet.blessed > 0) extra.blessed = true;
          const prio = CLASS_STAT_PROFILES[classId]?.priority;
          if (slot !== 'trinket' && prio && prio.length >= 2 && p.wallet.moltskin >= AUTO_CRAFT_MOLTSKIN) {
            extra.moltskin = AUTO_CRAFT_MOLTSKIN;
            extra.stats = [prio[0]!, prio[1]!];
          }
        } else if (payer === 'satchel') {
          const v = p.characters[classId]?.satchel?.venom ?? 0;
          if (v > 0) extra.venom = Math.min(10, v);
        }
        const r = autoCraft(p, classId, slot, extra) ?? (Object.keys(extra).length ? autoCraft(p, classId, slot) : null);
        if (r) {
          p = r.profile;
          rep.crafts++;
          if (r.equipped) rep.equipped++;
          opts.onRoll?.({ kind: 'craft', classId, payer, roll: r.roll });
        }
      }
    }
  }

  // 4b. Spare Serpent's Eyes: the player hand-crafts legendaries for companions without one
  //     (wallet-paid, tank → healer → dps), once the main wears its own.
  if (opts.craft !== false && mainManaged && reagents && hasUnlock(profileRating(p), 'legendaries')) {
    const mainWears = ITEM_SLOTS.some((s) => itemById(p, p.characters[p.mainClassId]?.gear[s])?.legendaryId);
    const order = classes.filter((c) => c !== p.mainClassId).sort((a, b) => ROLE_ORDER.indexOf(roleOf(a)) - ROLE_ORDER.indexOf(roleOf(b)) || a.localeCompare(b));
    for (const classId of mainWears ? order : []) {
      if (p.wallet.eyeFragments < 5) break;
      if (ITEM_SLOTS.some((s) => itemById(p, p.characters[classId]?.gear[s])?.legendaryId)) continue;
      const leg = legendariesFor(classId)[0];
      if (!leg) continue;
      const grade = bestCraftGrade(p, p.mainClassId, leg.slot);
      if (!grade) break;
      p = attuneForEye(p); // +econ-pacing
      const spec: CraftSpec = { classId, slot: leg.slot, grade, eye: true, payer: 'wallet' };
      if (p.wallet.venom >= AUTO_CRAFT_VENOM) spec.venom = AUTO_CRAFT_VENOM;
      const r = craft(p, spec);
      if (!r.ok) continue;
      p = wearIfBetter(r.profile, classId, r.item.id);
      rep.crafts++;
      if (p.characters[classId]?.gear[leg.slot] === r.item.id) rep.equipped++;
      opts.onRoll?.({ kind: 'craft', classId, payer: 'wallet', roll: r.roll });
    }
  }

  // 5. Salvage what nobody wears (legendaries are kept: they are rare and class-bound). Tier
  //    pieces give their Sigil back here, ready for step 6.
  if (opts.scrap !== false) {
    for (const it of unequippedItems(p)) {
      if (it.legendaryId) continue;
      const r = salvage(p, it.id);
      if (r.ok) {
        p = r.profile;
        rep.scrapped++;
      }
    }
  }

  // 6. Tier sets: the main character first, then companions (tank → healer → dps) once
  //    shareSigils is on (default: once the main wears 4 pieces), keeping SIGIL_RESERVE.
  if (opts.sigils !== false) {
    const order = [...classes].sort((a, b) => (a === p.mainClassId ? -1 : b === p.mainClassId ? 1 : ROLE_ORDER.indexOf(roleOf(a)) - ROLE_ORDER.indexOf(roleOf(b)) || a.localeCompare(b)));
    for (const classId of order) {
      const isMain = classId === p.mainClassId;
      const share = p.settings.shareSigils ?? setPieces(equippedGear(p, p.mainClassId), p.mainClassId) >= 4;
      if (!isMain && !share) continue;
      const reserve = isMain ? 0 : SIGIL_RESERVE;
      // Highest-ilvl tier slots first: they keep the bonus the longest.
      const slots = [...TIER_SLOTS].sort((a, b) => (itemById(p, p.characters[classId]?.gear[b])?.ilvl ?? 0) - (itemById(p, p.characters[classId]?.gear[a])?.ilvl ?? 0));
      for (const slot of slots) {
        if (setPieces(equippedGear(p, classId), classId) >= 4) break;
        if (p.wallet.sigils <= reserve) break;
        const cur = itemById(p, p.characters[classId]?.gear[slot]);
        if (!cur || cur.legendaryId || cur.setId === TIER_SET_IDS[classId]) continue;
        const r = catalyze(p, cur.id, classId);
        if (r.ok) {
          p = r.profile;
          rep.tierPieces++;
        }
      }
    }
  }

  // 7. Temper: one attempt at a time on the lowest-ilvl item per payer (ties: class, slot).
  if (opts.upgrade !== false) {
    const cap = capOf(p);
    const blocked = new Set<string>();
    for (let n = 0; n < MAX_ATTEMPTS; n++) {
      let pick: { item: Item; classId: ClassId } | undefined;
      for (const classId of classes) {
        const payer = payerOf(p, classId);
        if (blocked.has(classId) || (payer === 'wallet' && !mainManaged)) continue;
        for (const slot of ITEM_SLOTS) {
          const it = itemById(p, p.characters[classId]?.gear[slot]);
          if (it && it.ilvl < cap && (!pick || it.ilvl < pick.item.ilvl)) pick = { item: it, classId };
        }
      }
      if (!pick) break;
      const payer = payerOf(p, pick.classId);
      const boost: { venom?: number } = {};
      if (payer === 'wallet' && reagents && p.wallet.venom > TEMPER_VENOM_RESERVE) {
        const chance = temperOdds(p, pick.item).odds.chance;
        const v = Math.min(TEMPER_VENOM_MAX, Math.ceil(Math.max(0, 100 - chance) / TEMPER_VENOM_PP), p.wallet.venom - TEMPER_VENOM_RESERVE);
        if (v > 0) boost.venom = v;
      }
      const r = temper(p, pick.item.id, payer === 'satchel' ? { payer, classId: pick.classId } : boost);
      if (!r.ok) {
        if (r.reason === 'notEnoughGold') rep.goldBlocked = true;
        blocked.add(pick.classId);
        continue;
      }
      p = r.profile;
      rep.temperAttempts++;
      opts.onRoll?.({ kind: 'temper', classId: pick.classId, payer, roll: r.roll });
      if (r.roll.outcome === 'fail') rep.temperFails++;
      else rep.upgradeSteps += r.roll.outcome === 'surge' ? 2 : 1;
    }
  }

  // 7b. Rarity re-craft (the plateau sink): an item already at the tier cap and below the
  //     payer's RECRAFT_TARGET rarity is re-crafted at the lowest grade that keeps its item
  //     level, from what sits above RECRAFT_RESERVE_FRACTION of the tier-up reserve. The main commits Venom (and a Sigil on
  //     its own tier pieces, which salvaging the old piece gives back); companions roll base
  //     odds and leave their tier pieces alone. The loser is salvaged.
  if (opts.craft !== false) {
    const cap = capOf(p);
    for (const classId of classes) {
      const payer = payerOf(p, classId);
      if (payer === 'wallet' && !mainManaged) continue;
      const target = RARITY_RANK.indexOf(RECRAFT_TARGET[payer]);
      for (let n = 0; n < RECRAFT_PER_PASS; n++) {
        let pick: Item | undefined;
        for (const slot of ITEM_SLOTS) {
          const it = itemById(p, p.characters[classId]?.gear[slot]);
          if (!it || it.legendaryId || it.ilvl < cap || RARITY_RANK.indexOf(it.rarity) >= target) continue;
          const own = it.setId === TIER_SET_IDS[classId];
          if (own && (payer === 'satchel' || p.wallet.sigils < 1)) continue;
          if (!pick || RARITY_RANK.indexOf(it.rarity) < RARITY_RANK.indexOf(pick.rarity)) pick = it;
        }
        if (!pick) break;
        const grade = SCALE_GRADES.find((g) => Math.min(cap, GRADE_CRAFT_ILVL[g]) >= pick!.ilvl);
        if (!grade) break;
        // A Vault Forge Token that keeps the item level is free: no reserve check.
        const token = payer === 'wallet' ? p.wallet.vaultTokens.findIndex((t) => Math.min(cap, GRADE_CRAFT_ILVL[t.grade]) >= pick!.ilvl) : -1;
        if (token < 0) {
          const cost = craftCost(pick.slot, grade);
          const full = tierUpReserve(p, [classId]);
          const reserve = { fangsteel: full.fangsteel * RECRAFT_RESERVE_FRACTION, scales: full.scales * RECRAFT_RESERVE_FRACTION, gold: full.gold * RECRAFT_RESERVE_FRACTION };
          const have = scalesOf(p, classId);
          const scalesFrom = SCALE_GRADES.slice(SCALE_GRADES.indexOf(grade)).reduce((t, g) => t + have[g], 0);
          const fs = payer === 'wallet' ? p.wallet.fangsteel : (p.characters[classId]?.satchel?.fangsteel ?? 0);
          const gold = payer === 'wallet' ? p.currencies.gold : Infinity;
          if (fs - cost.fangsteel < reserve.fangsteel || gold - cost.gold < reserve.gold || scalesFrom - CRAFT_SCALES < reserve.scales) break;
        }
        const spec: CraftSpec = { classId, slot: pick.slot, grade: token >= 0 ? p.wallet.vaultTokens[token]!.grade : grade, payer, ...(token >= 0 ? { token } : {}) };
        if (payer === 'wallet' && reagents) {
          spec.venom = Math.min(10, p.wallet.venom);
          if (p.wallet.blessed > 0) spec.blessed = true;
          const prio = CLASS_STAT_PROFILES[classId]?.priority;
          if (pick.slot !== 'trinket' && prio && prio.length >= 2 && p.wallet.moltskin >= AUTO_CRAFT_MOLTSKIN) {
            spec.moltskin = AUTO_CRAFT_MOLTSKIN;
            spec.stats = [prio[0]!, prio[1]!];
          }
        }
        if (pick.setId === TIER_SET_IDS[classId]) spec.sigil = true;
        const r = craft(p, spec);
        if (!r.ok) {
          if (r.reason === 'notEnoughGold') rep.goldBlocked = true;
          break;
        }
        p = wearIfBetter(r.profile, classId, r.item.id);
        rep.crafts++;
        opts.onRoll?.({ kind: 'craft', classId, payer, roll: r.roll });
        const worn = p.characters[classId]?.gear[pick.slot] === r.item.id;
        if (worn) rep.equipped++;
        const loser = worn ? pick.id : r.item.id;
        if (opts.scrap !== false) {
          const s = salvage(p, loser);
          if (s.ok) {
            p = s.profile;
            rep.scrapped++;
          }
        }
      }
    }
  }

  // 8. Gems: fuse the role colours up to the rank cap, then socket every character.
  const maxRank = profileMaxGemRank(p);
  if (maxRank > 0) {
    const colors = [...new Set(classes.map((c) => ROLE_GEM_COLOR[roleOf(c)]))];
    for (const color of colors) {
      for (let rank = 1; rank < maxRank; rank++) {
        for (;;) {
          if (fuseGoldCost(rank) > p.currencies.gold) break;
          const r = fuseGems(p, color, rank);
          if (!r.ok) break;
          p = r.profile;
          rep.fuses++;
        }
      }
    }
    for (const classId of classes) {
      const before = JSON.stringify(equippedGear(p, classId));
      const r = autoGemCharacter(p, classId);
      if (r.ok) {
        p = r.profile;
        rep.gemsSocketed ||= JSON.stringify(equippedGear(p, classId)) !== before;
      }
    }
  }

  // 9. Reforge: items at the tier cap take the swap that raises their score most. Cheap.
  if (opts.reforge !== false && hasUnlock(profileRating(p), 'reforge')) {
    const cap = capOf(p);
    for (const classId of classes) {
      for (const slot of ITEM_SLOTS) {
        const it = itemById(p, p.characters[classId]?.gear[slot]);
        if (!it || it.reforged || it.ilvl < cap) continue;
        if (p.wallet.fangsteel < REFORGE_FANGSTEEL || p.currencies.gold < REFORGE_GOLD) break;
        const best = bestReforge(it, classId);
        if (!best) continue;
        const r = reforgeItem(p, it.id, best.index, best.to);
        if (r.ok) {
          p = r.profile;
          rep.reforges++;
        }
      }
    }
  }

  // 10. Masterwork: only what sits above the tier-up reserve (0 at the top tier), lowest first.
  if (opts.masterwork !== false) {
    const cap = capOf(p);
    // A character whose purse is at its reserve is skipped, not the whole pass (companions
    // sort first, so a `break` here used to starve the main character's masterwork).
    const mwBlocked = new Set<ClassId>();
    for (let n = 0; n < MAX_ATTEMPTS; n++) {
      let pick: { item: Item; classId: ClassId } | undefined;
      for (const classId of classes) {
        if ((payerOf(p, classId) === 'wallet' && !mainManaged) || mwBlocked.has(classId)) continue;
        for (const slot of ITEM_SLOTS) {
          const it = itemById(p, p.characters[classId]?.gear[slot]);
          if (!it || it.ilvl < cap || (it.masterwork ?? 0) >= masterworkCeiling(it)) continue;
          if (!pick || (it.masterwork ?? 0) < (pick.item.masterwork ?? 0)) pick = { item: it, classId };
        }
      }
      if (!pick) break;
      const payer = payerOf(p, pick.classId);
      // Each satchel only reserves for its own companion; the wallet for the main.
      const reserve = tierUpReserve(p, [pick.classId]);
      const step = MW_COST[Math.min(MW_COST.length - 1, pick.item.masterwork ?? 0)]!;
      const fs = payer === 'wallet' ? p.wallet.fangsteel : (p.characters[pick.classId]?.satchel?.fangsteel ?? 0);
      const gold = payer === 'wallet' ? p.currencies.gold : Infinity;
      if (fs - step.fangsteel < reserve.fangsteel || gold - step.gold < reserve.gold) {
        mwBlocked.add(pick.classId);
        continue;
      }
      const r = masterworkItem(p, pick.item.id, payer === 'satchel' ? { payer, classId: pick.classId } : {});
      if (!r.ok) {
        if (r.reason === 'notEnoughGold') rep.goldBlocked = true;
        mwBlocked.add(pick.classId);
        continue;
      }
      p = r.profile;
      if (r.roll.outcome !== 'fail') rep.masterworkSteps++;
      opts.onRoll?.({ kind: 'masterwork', classId: pick.classId, payer, roll: r.roll });
    }
  }

  // 10b. +econ-pacing: late plateau sinks (crafting.md §4.6), main auto-managed only. Everything
  //      here is keep-or-replace or pity, so it never makes gear worse.
  if (mainManaged && reagents && opts.craft !== false) {
    const cap = capOf(p);
    const goldReserve = tierUpReserve(p, [p.mainClassId]).gold;
    const capped = (classId: ClassId): Item[] =>
      ITEM_SLOTS.map((s) => itemById(p, p.characters[classId]?.gear[s])).filter((it): it is Item => !!it && !it.legendaryId && it.ilvl >= cap);
    const mainFirst = [...classes].sort((a, b) => (a === p.mainClassId ? -1 : b === p.mainClassId ? 1 : ROLE_ORDER.indexOf(roleOf(a)) - ROLE_ORDER.indexOf(roleOf(b)) || a.localeCompare(b)));
    // Sigil Reshape: once nobody managed wants Sigils, spare ones reroll capped tier pieces: a
    // piece whose stats miss the class's top two first, then the lowest total quality. The best
    // top-two secondary is kept, so the other one rerolls (stat and quality).
    if (!wantsSigils(p, classes)) {
      for (let n = 0; n < RESHAPES_PER_PASS && p.wallet.sigils > RESHAPE_SIGIL_RESERVE; n++) {
        let pick: { item: Item; classId: ClassId; key: number; keepIndex: number } | undefined;
        for (const classId of mainFirst) {
          const top = (CLASS_STAT_PROFILES[classId]?.priority ?? SECONDARY_STATS).slice(0, 2);
          for (const it of capped(classId)) {
            if (it.setId !== TIER_SET_IDS[classId]) continue;
            const offStats = it.secondaries.filter((x) => !top.includes(x.stat)).length;
            const q = it.secondaries.reduce((t, x) => t + (x.quality ?? 1), 0);
            if (!offStats && q >= it.secondaries.length * QUALITY_MAX - 1e-9) continue;
            const key = offStats * 10 - q; // off-priority stats first, then lowest quality
            let keepIndex = -1;
            it.secondaries.forEach((x, i) => {
              if (top.includes(x.stat) && (keepIndex < 0 || (x.quality ?? 1) > (it.secondaries[keepIndex]!.quality ?? 1))) keepIndex = i;
            });
            if (!pick || key > pick.key) pick = { item: it, classId, key, keepIndex };
          }
        }
        if (!pick) break;
        const o = sigilReshapeOdds(p, pick.item);
        if (o.blocked || p.currencies.gold - (o.cost.gold ?? 0) < goldReserve) break;
        const r = sigilReshape(p, pick.item.id, pick.keepIndex >= 0 ? { keepIndex: pick.keepIndex } : {});
        if (!r.ok) break;
        p = keepIfBetter(r.profile, pick.classId, pick.item);
        rep.reshapes++;
      }
    }
    // Moltskin Refine: reroll the qualities of capped items (lowest total quality first).
    for (let n = 0; n < REFINES_PER_PASS; n++) {
      if (p.wallet.moltskin <= REFINE_MOLTSKIN_RESERVE) break;
      let pick: { item: Item; classId: ClassId; q: number } | undefined;
      for (const classId of mainFirst) {
        for (const it of capped(classId)) {
          const q = it.secondaries.reduce((t, x) => t + (x.quality ?? 1), 0);
          if (q >= it.secondaries.length * QUALITY_MAX - 1e-9) continue;
          if (!pick || q < pick.q) pick = { item: it, classId, q };
        }
      }
      if (!pick) break;
      const cost = refineCost(pick.item, 1);
      if (p.currencies.gold - (cost.gold ?? 0) < goldReserve) break;
      const r = refine(p, pick.item.id);
      if (!r.ok) break;
      p = keepIfBetter(r.profile, pick.classId, pick.item);
      rep.refines++;
    }
    // Spare Eyes: once every managed character wears a legendary, craft the ones still missing
    // from the collection (attuned to the guarantee first); wear-if-better.
    const allWear = classes.every((c) => ITEM_SLOTS.some((s) => itemById(p, p.characters[c]?.gear[s])?.legendaryId));
    if (allWear && hasUnlock(profileRating(p), 'legendaries')) {
      const owned = new Set(p.inventory.map((i) => i.legendaryId).filter(Boolean));
      for (const classId of mainFirst) {
        const leg = legendariesFor(classId).find((l) => !owned.has(l.id));
        if (!leg) continue;
        if (p.wallet.eyeFragments < EYE_FRAGMENTS_PER_EYE + EYE_ATTUNE_FRAGMENTS * (EYE_ATTUNE_STEPS - p.forge.eyeMisses)) break;
        p = attuneForEye(p);
        const grade = bestCraftGrade(p, p.mainClassId, leg.slot);
        if (!grade) break;
        const r = craft(p, { classId, slot: leg.slot, grade, eye: true, payer: 'wallet' });
        if (!r.ok) break;
        p = wearIfBetter(r.profile, classId, r.item.id);
        rep.crafts++;
        opts.onRoll?.({ kind: 'craft', classId, payer: 'wallet', roll: r.roll });
        if (r.item.legendaryId) owned.add(r.item.legendaryId);
        break; // one per pass
      }
    }
  }

  // 11. Talents: recommended loadout at the current points and rank tier cap.
  if (opts.talents !== false) {
    const maxTier = tierDef(profileStanding(p).tier).talentTiers;
    p = structuredClone(p);
    for (const classId of classes) {
      const ch = p.characters[classId];
      if (!ch) continue;
      const d = defaultLoadout(roleOf(classId), ch.hero.talentPoints, maxTier, classId);
      ch.loadouts = [d, ...ch.loadouts.filter((l) => l.id !== d.id)];
      ch.activeLoadoutId = d.id;
    }
  }

  return { profile: p, ...rep, goldSpent: goldBefore - p.currencies.gold };
}

/**
 * Fangsteel, Scales and gold to temper every equipped item of `classes` to the NEXT rank tier's
 * cap at base cost (0 at the top tier). Masterwork leaves this much in the purse.
 */
export function tierUpReserve(p: Profile, classes: readonly ClassId[] = Object.keys(p.characters)): { fangsteel: number; scales: number; gold: number } {
  const idx = RANK_TIERS.findIndex((t) => t.tier === profileStanding(p).tier);
  const next = RANK_TIERS[idx + 1];
  const out = { fangsteel: 0, scales: 0, gold: 0 };
  if (!next) return out;
  for (const classId of classes) {
    for (const slot of ITEM_SLOTS) {
      const it = itemById(p, p.characters[classId]?.gear[slot]);
      if (!it) continue;
      for (let l = it.ilvl + 5; l <= next.maxIlvl; l += 5) {
        const c = TEMPER_COST[gradeForIlvl(l)];
        out.fangsteel += c.fangsteel;
        out.scales += c.scales;
        if (classId === p.mainClassId) out.gold += c.gold;
      }
    }
  }
  return out;
}

/** A managed character still wants Sigils (under 4 pieces; companions only once Sigils are shared). */
function wantsSigils(p: Profile, classes: readonly ClassId[]): boolean {
  const mainDone = setPieces(equippedGear(p, p.mainClassId), p.mainClassId) >= 4;
  if (!mainDone) return true;
  const share = p.settings.shareSigils ?? true;
  return share && classes.some((c) => c !== p.mainClassId && setPieces(equippedGear(p, c), c) < 4);
}

/** The reforge that raises the item's score most for `classId` (null if none helps). */
export function bestReforge(it: Item, classId: ClassId): { index: number; to: SecondaryStat } | null {
  let best: { index: number; to: SecondaryStat } | null = null;
  let bestGain = 1e-9;
  const base = itemScore(it, classId);
  for (let index = 0; index < it.secondaries.length; index++) {
    for (const to of SECONDARY_STATS) {
      const r = reforge(it, index, to);
      if (!r.ok) continue;
      const gain = itemScore(r.item, classId) - base;
      if (gain > bestGain) {
        best = { index, to };
        bestGain = gain;
      }
    }
  }
  return best;
}

function wearable(p: Profile, classId: ClassId, it: Item): boolean {
  if (itemOwner(p, it.id)) return false;
  if (!it.legendaryId) return true;
  if (legendary(it.legendaryId)?.classId !== classId) return false;
  const ch = p.characters[classId]!;
  let worn = 0;
  for (const s of ITEM_SLOTS) if (s !== it.slot && itemById(p, ch.gear[s])?.legendaryId) worn++;
  return worn < MAX_EQUIPPED_LEGENDARIES;
}

/** +econ-pacing: Eye Attunement steps from 0 misses to the guarantee. */
const EYE_ATTUNE_STEPS = 3;

/**
 * +econ-pacing: before an Eye craft, spend spare fragments on Eye Attunement (crafting.md §4.6)
 * while a full Eye stays affordable: a player with a pile of fragments buys the guarantee.
 */
function attuneForEye(p: Profile): Profile {
  for (let i = 0; i < EYE_ATTUNE_STEPS; i++) {
    const o = eyeAttuneOdds(p);
    if (o.blocked || p.wallet.eyeFragments - EYE_ATTUNE_FRAGMENTS < EYE_FRAGMENTS_PER_EYE) break;
    const r = attuneEye(p);
    if (!r.ok) break;
    p = r.profile;
  }
  return p;
}

/** +econ-pacing: resolve a pending Refine / Reshape: keep it only if the item scores higher. */
function keepIfBetter(p: Profile, classId: ClassId, before: Item): Profile {
  const kept = keepRefine(p);
  if (!kept.ok) return p;
  const after = itemById(kept.profile, before.id);
  if (after && itemScore(after, classId) > itemScore(before, classId)) return kept.profile;
  const d = discardRefine(p);
  return d.ok ? d.profile : p;
}
